/**
 * Settings → Data: "Export my data" (LGPD art. 18 / GDPR art. 15 and 20: a .zip of JSON + Markdown with my profile,
 * my messages, my files list and the pages I created, in every workspace) and, for owners and admins, "Export
 * workspace" (members, public channels as JSON, shared pages as Markdown). The server builds the file right away and
 * keeps it for a day; its download link is short-lived (the latest one is fetched again on load). One export per
 * scope every 10 minutes (`nextAllowedAt`). Account deletion lives under Account. Online only.
 */
import type { DataExport, DataExportResponse, DataExportScope } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isManager } from "../logic/roles.ts";
import { errorKind } from "../ports.ts";
import type { SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ExportSlot = {
  status: "loading" | "ready" | "error";
  /** The latest kept export (absent when none). */
  latest?: DataExport | undefined;
  /** Building one now. */
  busy: boolean;
  /** Not before then (the 10-minute limit). */
  nextAllowedAt?: number | undefined;
  /** Requesting is possible now. */
  canRequest: boolean;
};

export type DataSettingsState = {
  account: ExportSlot;
  /** Owners and admins only. */
  workspace?: ExportSlot;
  manager: boolean;
  notice?: MessageDescriptor;
};

const EMPTY: ExportSlot = { status: "loading", busy: false, canRequest: false };

export class DataSettingsViewModel extends ViewModel<DataSettingsState> {
  private readonly deps: SettingsDeps;
  readonly ws: WorkspaceViewModel;

  constructor(ws: WorkspaceViewModel, deps: SettingsDeps) {
    super({ account: EMPTY, manager: false });
    this.ws = ws;
    this.deps = deps;
    this.onDispose(ws.subscribe(() => this.follow()));
    this.follow();
  }

  private now(): number {
    return this.ws.deps.clock.now();
  }

  private follow(): void {
    const me = this.ws.state.cache?.members[this.ws.state.meId];
    const manager = me !== undefined && isManager(me, this.now());
    if (manager === this.state.manager) return;
    this.update({ manager, workspace: manager ? (this.state.workspace ?? EMPTY) : undefined });
    if (manager && this.state.workspace?.status === "loading") void this.loadScope("workspace");
  }

  private slot(scope: DataExportScope): ExportSlot | undefined {
    return scope === "account" ? this.state.account : this.state.workspace;
  }

  private setSlot(scope: DataExportScope, patch: Partial<ExportSlot>): void {
    const prev = this.slot(scope) ?? EMPTY;
    const next = { ...prev, ...patch };
    next.canRequest = next.status === "ready" && !next.busy && (next.nextAllowedAt === undefined || next.nextAllowedAt <= this.now());
    this.update(scope === "account" ? { account: next } : { workspace: next });
  }

  private adopt(scope: DataExportScope, res: DataExportResponse): void {
    const patch: Partial<ExportSlot> = { status: "ready", latest: res.export, nextAllowedAt: res.nextAllowedAt };
    this.setSlot(scope, patch);
  }

  async load(): Promise<void> {
    await Promise.all([this.loadScope("account"), this.state.manager ? this.loadScope("workspace") : Promise.resolve()]);
  }

  private async loadScope(scope: DataExportScope): Promise<void> {
    try {
      const res = scope === "account" ? await this.deps.api.accountExport() : await this.deps.api.workspaceExport(this.ws.wsId);
      if (!this.isDisposed) this.adopt(scope, res);
    } catch {
      if (!this.isDisposed && this.slot(scope)?.status !== "ready") this.setSlot(scope, { status: "error" });
    }
  }

  /** Builds a new export (the button); its download link replaces the previous one. */
  async request(scope: DataExportScope): Promise<void> {
    const slot = this.slot(scope);
    if (slot === undefined || slot.busy) return;
    this.setSlot(scope, { busy: true });
    try {
      const res = scope === "account" ? await this.deps.api.createAccountExport() : await this.deps.api.createWorkspaceExport(this.ws.wsId);
      if (this.isDisposed) return;
      this.adopt(scope, res);
      this.update({ notice: msg("data.export.ready") });
    } catch (e) {
      if (this.isDisposed) return;
      const kind = errorKind(e);
      this.update({ notice: kind === "rateLimited" ? msg("data.export.tooSoon") : kind === "network" ? msg("settings.offline") : msg("data.export.failed") });
      if (kind === "rateLimited") void this.loadScope(scope);
    } finally {
      if (!this.isDisposed) this.setSlot(scope, { busy: false });
    }
  }

  /** The kept export's link is still good (else load() fetches a fresh one). */
  linkValid(scope: DataExportScope): boolean {
    const latest = this.slot(scope)?.latest;
    return latest !== undefined && latest.urlExpiresAt > this.now();
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
